// src/components/alianzas/ui.js
// Primitivas visuales del area de Alianzas, compartidas entre Seguimiento y
// Certificaciones para que el estilo no se desincronice entre ambas paginas.
import React, { useEffect } from 'react';
import { AlertCircle, CheckCircle, Loader2, Trash2, X } from 'lucide-react';

export const ACCENT = '#00B8FF';
export const ACCENT_RGB = '0,184,255';
export const tint = (alpha) => `rgba(${ACCENT_RGB},${alpha})`;
export const BORDER = tint(0.25);

export const PANEL_BG = 'linear-gradient(135deg,#04141f 0%,#071f30 50%,#04141f 100%)';

export const PAGE_BG =
  `radial-gradient(1100px 600px at 15% -10%, ${tint(0.12)} 0%, transparent 60%),` +
  'radial-gradient(900px 500px at 100% 0%, rgba(134,68,255,0.10) 0%, transparent 55%),' +
  'linear-gradient(135deg, #08203a 0%, #0b2b44 50%, #04121f 100%)';

export const CARD =
  'rounded-2xl border border-sky-400/20 bg-[#061a2b]/60 backdrop-blur-sm shadow-lg shadow-black/20';

export const BTN_PRIMARY = {
  background: tint(0.2),
  border: `1px solid ${BORDER}`,
  color: ACCENT,
};

export const inputCls =
  'rounded-lg px-3 py-2 text-sm bg-white/5 border border-sky-400/20 text-white placeholder-white/30 focus:outline-none focus:border-sky-300/60';
export const selectCls =
  'rounded-lg px-3 py-2 text-sm bg-[#071f30] border border-sky-400/20 text-white focus:outline-none focus:border-sky-300/60';

// Colores de marca por fabricante, reutilizados en badges, gráficas y donuts
// de toda el área de Alianzas (Certificaciones, Dashboard y QBR).
export const FABRICANTE_COLORS = {
  AWS: '#FF9900',
  Google: '#4285F4',
  Microsoft: '#00A4EF',
  IBM: '#0F62FE',
  Huawei: '#C7000B',
  Oracle: '#C74634',
  'Alibaba Cloud': '#FF6A00',
  Linux: '#F5C518',
  FinOps: '#22C55E',
  ITIL: '#8B5CF6',
};

const FALLBACK_PALETTE = [ACCENT, '#8644FF', '#00D68F', '#F59E0B', '#F43F8E', '#7DD3FC'];

export const colorForFabricante = (nombre, index = 0) =>
  FABRICANTE_COLORS[nombre] || FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];

export const iniciales = (nombre = '') =>
  nombre.split(' ').slice(0, 2).map(p => p[0] || '').join('').toUpperCase();

export const fmtFecha = (value) => {
  if (!value) return '—';
  const fechaCalendario = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = new Date(fechaCalendario ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    ...(fechaCalendario ? { timeZone: 'UTC' } : {}),
  });
};

export const fmtFechaHora = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export function Badge({ value, map = {}, fallbackLabel }) {
  if (!value) return <span className="text-white/25 text-xs">—</span>;
  const cfg = map[value] || { bg: 'rgba(100,116,139,0.2)', color: '#cbd5e1' };
  return (
    <span
      className="px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.label || fallbackLabel || value}
    </span>
  );
}

export function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-sky-200/80">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

export function Toast({ msg, type, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3500);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const isOk = type === 'ok';
  return (
    <div
      className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium shadow-2xl"
      style={{
        background: isOk ? 'rgba(74,222,128,0.15)' : 'rgba(239,68,68,0.15)',
        border: `1px solid ${isOk ? 'rgba(74,222,128,0.4)' : 'rgba(239,68,68,0.4)'}`,
        color: isOk ? '#4ade80' : '#f87171',
      }}
    >
      {isOk ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
      {msg}
    </div>
  );
}

export function ModalShell({ title, icon, onClose, children, wide }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className={`relative w-full ${wide ? 'max-w-3xl' : 'max-w-2xl'} rounded-2xl shadow-2xl overflow-hidden`}
        style={{ background: PANEL_BG, border: `1px solid ${BORDER}` }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-sky-400/15">
          <div className="flex items-center gap-2">
            {icon}
            <span className="font-semibold text-white">{title}</span>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ConfirmDialog({ title, message, confirmLabel, busy, onClose, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6"
        style={{ background: PANEL_BG, border: '1px solid rgba(239,68,68,0.3)' }}
      >
        <div className="flex items-center gap-2 mb-3 text-red-400">
          <AlertCircle size={18} />
          <span className="font-semibold">{title}</span>
        </div>
        <p className="text-sm text-white/70 mb-4">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-white/15 text-white/60 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 transition-all disabled:opacity-50"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
